import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const portfolioRuleSchema = z.object({
  name: z.string().min(1, "Name is required").max(100),
  stop_loss: z.object({
    enabled: z.boolean().default(false),
    trigger_percent: z.coerce.number().min(-100).max(0).optional().nullable().default(null),
    action: z.enum(["close_all", "reduce_position", "notify"]).optional().default("notify"),
  }).default({}),
  take_profit: z.object({
    enabled: z.boolean().default(false),
    trigger_percent: z.coerce.number().min(0).max(1000).optional().nullable().default(null),
    action: z.enum(["close_all", "reduce_position", "notify"]).optional().default("notify"),
  }).default({}),
  check_interval: z.coerce.number().refine(v => [5, 15, 30, 60].includes(v), "Must be 5, 15, 30, or 60"),
  notify_on_trigger: z.boolean().default(true),
}).superRefine((data, ctx) => {
  if (data.stop_loss?.enabled && (data.stop_loss?.trigger_percent == null)) {
    ctx.addIssue({ code: "custom", path: ["stop_loss", "trigger_percent"], message: "Required when stop loss is enabled" });
  }
  if (data.take_profit?.enabled && (data.take_profit?.trigger_percent == null)) {
    ctx.addIssue({ code: "custom", path: ["take_profit", "trigger_percent"], message: "Required when take profit is enabled" });
  }
});

export default function PortfolioRuleSheet({
  open,
  onOpenChange,
  onSubmit,
  editingRule,
}) {
  const isEdit = !!editingRule;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(portfolioRuleSchema),
    defaultValues: {
      name: "",
      stop_loss: { enabled: false, trigger_percent: null, action: "notify" },
      take_profit: { enabled: false, trigger_percent: null, action: "notify" },
      check_interval: 15,
      notify_on_trigger: true,
    },
  });

  useEffect(() => {
    if (editingRule) {
      reset({
        name: editingRule.name || "",
        stop_loss: {
          enabled: editingRule.stop_loss?.enabled ?? false,
          trigger_percent: editingRule.stop_loss?.trigger_percent ?? null,
          action: editingRule.stop_loss?.action || "notify",
        },
        take_profit: {
          enabled: editingRule.take_profit?.enabled ?? false,
          trigger_percent: editingRule.take_profit?.trigger_percent ?? null,
          action: editingRule.take_profit?.action || "notify",
        },
        check_interval: editingRule.check_interval || 15,
        notify_on_trigger: editingRule.notify_on_trigger ?? true,
      });
    } else {
      reset({
        name: "",
        stop_loss: { enabled: false, trigger_percent: null, action: "notify" },
        take_profit: { enabled: false, trigger_percent: null, action: "notify" },
        check_interval: 15,
        notify_on_trigger: true,
      });
    }
  }, [editingRule, reset]);

  const onFormSubmit = async (data) => {
    const success = await onSubmit(data);
    if (success) onOpenChange(false);
  };

  const slEnabled = watch("stop_loss.enabled");
  const tpEnabled = watch("take_profit.enabled");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-surface-2 border-border w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isEdit ? "Edit Portfolio Rule" : "Create Portfolio Rule"}</SheetTitle>
          <SheetDescription>
            {isEdit ? "Update your portfolio rule configuration." : "Configure a new portfolio management rule."}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-5 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input id="name" {...register("name")} placeholder="My Rule" className="rounded-sm bg-surface-1 border-border" />
            {errors.name && <p className="text-xs text-loss">{errors.name.message}</p>}
          </div>

          {/* Stop Loss Section */}
          <div className="border border-border rounded-sm p-4 space-y-3 bg-surface-1">
            <div className="flex items-center justify-between">
              <Label htmlFor="sl_enabled" className="font-semibold text-sm cursor-pointer">Stop Loss</Label>
              <Switch
                id="sl_enabled"
                checked={slEnabled}
                onCheckedChange={(v) => setValue("stop_loss.enabled", v, { shouldValidate: true })}
              />
            </div>
            {slEnabled && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="sl_trigger">Trigger % (negative)</Label>
                  <Input
                    id="sl_trigger"
                    type="number"
                    step="0.1"
                    {...register("stop_loss.trigger_percent")}
                    placeholder="-5"
                    className="rounded-sm bg-surface-2 border-border font-mono text-xs"
                  />
                  {errors.stop_loss?.trigger_percent && (
                    <p className="text-xs text-loss">{errors.stop_loss.trigger_percent.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sl_action">Action</Label>
                  <Select
                    value={watch("stop_loss.action")}
                    onValueChange={(v) => setValue("stop_loss.action", v)}
                  >
                    <SelectTrigger className="rounded-sm bg-surface-2 border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-surface-2 border-border">
                      <SelectItem value="close_all">Close All</SelectItem>
                      <SelectItem value="reduce_position">Reduce Position</SelectItem>
                      <SelectItem value="notify">Notify</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>

          {/* Take Profit Section */}
          <div className="border border-border rounded-sm p-4 space-y-3 bg-surface-1">
            <div className="flex items-center justify-between">
              <Label htmlFor="tp_enabled" className="font-semibold text-sm cursor-pointer">Take Profit</Label>
              <Switch
                id="tp_enabled"
                checked={tpEnabled}
                onCheckedChange={(v) => setValue("take_profit.enabled", v, { shouldValidate: true })}
              />
            </div>
            {tpEnabled && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="tp_trigger">Trigger % (positive)</Label>
                  <Input
                    id="tp_trigger"
                    type="number"
                    step="0.1"
                    {...register("take_profit.trigger_percent")}
                    placeholder="5"
                    className="rounded-sm bg-surface-2 border-border font-mono text-xs"
                  />
                  {errors.take_profit?.trigger_percent && (
                    <p className="text-xs text-loss">{errors.take_profit.trigger_percent.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tp_action">Action</Label>
                  <Select
                    value={watch("take_profit.action")}
                    onValueChange={(v) => setValue("take_profit.action", v)}
                  >
                    <SelectTrigger className="rounded-sm bg-surface-2 border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-surface-2 border-border">
                      <SelectItem value="close_all">Close All</SelectItem>
                      <SelectItem value="reduce_position">Reduce Position</SelectItem>
                      <SelectItem value="notify">Notify</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>

          {/* Check Interval */}
          <div className="space-y-2">
            <Label htmlFor="check_interval">Check Interval (minutes)</Label>
            <Select
              value={String(watch("check_interval"))}
              onValueChange={(v) => setValue("check_interval", Number(v), { shouldValidate: true })}
            >
              <SelectTrigger className="rounded-sm bg-surface-1 border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-surface-2 border-border">
                <SelectItem value="5">5 minutes</SelectItem>
                <SelectItem value="15">15 minutes</SelectItem>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="60">60 minutes</SelectItem>
              </SelectContent>
            </Select>
            {errors.check_interval && <p className="text-xs text-loss">{errors.check_interval.message}</p>}
          </div>

          {/* Notify on Trigger */}
          <div className="flex items-center justify-between border border-border rounded-sm p-3 bg-surface-1">
            <Label htmlFor="notify" className="cursor-pointer">Notify on Trigger</Label>
            <Switch
              id="notify"
              checked={watch("notify_on_trigger")}
              onCheckedChange={(v) => setValue("notify_on_trigger", v)}
            />
          </div>

          <SheetFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-sm border-border"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" className="rounded-sm" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              {isEdit ? "Save Changes" : "Create Rule"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
